// @flow
import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import IconButton from '@material-ui/core/IconButton';
import Button from '@material-ui/core/Button';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import Typography from '@material-ui/core/Typography';
import TextField from '@material-ui/core/TextField';
import { withStyles } from '@material-ui/core/styles';
import tracker from 'utils/tracker';
import * as npsActions from 'data/nps/npsActions';
import withStdDialog from 'components/Dialogs/StdDialog';
import { setAuthSession as setAuthSessionAction } from 'utils/auth/actions';
import { getAuthSession } from 'utils/auth/selectors';
import { getIsWebfirstDataset } from 'data/datasets/selectors';
import type { AuthSession } from 'utils/auth/types';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { isAcme } from 'isAcme';

import { styles } from './styles';

type Props = {
  dispatchPostNPSSurveyResultAction: (any) => void,
  closeDialog: (Event) => void,
  onRef: (any) => void,
  authSession: AuthSession,
  setAuthSession: (AuthSession) => void,
  classes: Object,
  isWebFirstDataset: boolean,
}

type State = {
  feedback: any,
  score?: any,
  hoverScore?: any,
  thankYou: boolean,
}

class NPSSurveyDialog extends PureComponent<Props, State> {

  constructor(props) {
    super(props);
    this.state = {
      feedback: '',
      score: undefined,
      hoverScore: undefined,
      thankYou: false,
    };
  }

  componentDidMount() {
    this.props.onRef(this);
    tracker.track(tracker.events.nps, { status: 'start' });
  }

  handleClose = (event: Event, reason: string) => {

    let surveyResult;
    if (!this.state.thankYou) {
      this.props.setAuthSession(this.props.authSession.merge({ surveyRequested: false }));

      tracker.track(tracker.events.nps, {
        status: 'response',
        feedback: this.state.feedback.length > 0 ? this.state.feedback : undefined,
        rating: this.state.score,
      });

      if (reason === 'OK_BUTTON') {
        surveyResult = 'COMPLETED';
      } else if (!this.state.score) {
        surveyResult = 'ABANDONED';
      } else {
        surveyResult = 'CANCELLED';
      }
      this.props.dispatchPostNPSSurveyResultAction({ surveyResult });
    }

    if (surveyResult === 'COMPLETED') {
      this.setState({ thankYou: true });
      setTimeout(() => this.props.closeDialog(event), 2500);
    } else {
      this.props.closeDialog(event);
    }
  };

  renderThankYou() {
    return (
      <DialogContent>
        <CheckCircleOutlineIcon
          color="primary"
          style={{
            width: 64,
            height: 64,
            marginTop: 24,
            marginBottom: 24,
            display: 'block',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        />
        <Typography
          variant="h6"
          align="center"
          style={{
            marginBottom: 24,
          }}
        >
          Thank you.
        </Typography>
        <Typography
          variant="subtitle1"
          align="center"
        >
          Your feedback helps us discover new opportunities to improve {isAcme ? 'Simplifi by Quicken' : 'Quicken'} and make sure you have the best possible
          experience.
        </Typography>

      </DialogContent>
    );
  }

  renderNPS() {
    let message;
    if (isAcme) {
      message = 'How likely are you to recommend Simplifi by Quicken to a friend or colleague?';
    } else if (this.props.isWebFirstDataset) {
      message = 'How likely are you to recommend Quicken to a friend or colleague?'; // Quicken Web First
    } else {
      message = 'How likely are you to recommend Quicken on the Web to a friend or colleague who uses Quicken?'; // Quicken
    }
    return (
      <>
        <DialogTitle>
          {message}
        </DialogTitle>
        <DialogContent>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'nowrap',
            }}
          >
            { [...Array(11).keys()].map((score) => (
              <IconButton
                style={{
                  border: score === this.state.score ? 'solid 2px' : 'solid 1px',
                  width: 30,
                  height: 30,
                  fontSize: 14,
                }}
                key={score}
                id={`nps-score-${score}`}
                aria-label={score}
                color={score === this.state.hoverScore || score === this.state.score ? 'primary' : 'default'}
                onClick={() => this.setState({ score })}
                onMouseOver={() => this.setState({ hoverScore: score })}
                onFocus={() => this.setState({ hoverScore: score })}
                onMouseOut={() => this.setState({ hoverScore: undefined })}
                onBlur={() => this.setState({ hoverScore: undefined })}
              >
                <div style={{ position: 'absolute' }}>
                  {score}
                </div>
              </IconButton>
            )) }
          </div>

          <Typography
            variant="caption"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              flexWrap: 'nowrap',
              marginTop: 8,
            }}
          >
            <span>
              Not likely at all
            </span>
            <span>
              Extremely likely
            </span>
          </Typography>

          <br />

          {this.state.score !== undefined &&
          <TextField
            label="What is the primary reason for your score?"
            id="nps-feedback"
            aria-label="What is the primary reason for your score?"
            multiline
            value={this.state.feedback}
            onChange={(event) => this.setState({ feedback: event.target.value })}
            margin="normal"
            fullWidth
            inputProps={{ maxLength: 255 }}
            FormHelperTextProps={{ className: this.props.classes.caption }}
            helperText={`You can enter up to 255 characters (${255 - this.state.feedback.length} remaining).`}
            rows="4"
            rowsMax="4"
            variant="outlined"
          />
          }

        </DialogContent>

        {this.state.score !== undefined &&
        <DialogActions>
          <Button
            id="nps-send"
            aria-label="Send"
            color="primary"
            onClick={(event) => this.handleClose(event, 'OK_BUTTON')}
          >
            Submit
          </Button>
        </DialogActions>
        }

      </>
    );
  }

  render() {
    return this.state.thankYou ? this.renderThankYou() : this.renderNPS();
  }
}

function mapStateToProps(state) {
  return ({
    authSession: getAuthSession(state),
    isWebFirstDataset: getIsWebfirstDataset(state),
  });
}

function mapDispatchToProps(dispatch) {
  return {
    setAuthSession: (session) => dispatch(setAuthSessionAction(session)),
    dispatchPostNPSSurveyResultAction: (data) => dispatch(npsActions.postNPSSurveyResultAction(data)),
  };
}

export default compose(
  withStdDialog(),
  withStyles(styles, {withTheme: true}),
  connect(mapStateToProps, mapDispatchToProps),
)(NPSSurveyDialog);
